/** ***********************************************************************
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

/* eslint-disable react/jsx-wrap-multilines */
import { observer } from 'mobx-react';
import React, { Component } from 'react';
import Button from '@react/react-spectrum/Button';
import uuid from 'uuid';
import ProductCTA from './productPrimaryCTA';
import './layout.scss';

const validateVitalData = ({ textList, imageList }) => (
    textList && textList.length > 3 && imageList && imageList.length);

const shouldShowProdCTA = (urlData, allAppsModel) => {
    if (urlData && urlData.showProductStatusCTA && urlData.sapcode) {
        return allAppsModel.filterApps({ sapcode: urlData.sapcode }).length;
    }
    return false;
};

const renderDefaultCTA = ({
    textList, onAction, urlList,
    name, ingestDescription, appConfig, appsSkeletonModel, analytics,
}) => {
    const { bannerAnalyticsCallback } = analytics;
    return (
        textList[2] && (
            <Button
                label={textList[2]}
                role="button"
                aria-label={textList[2]}
                variant="cta"
                onClick={() => {
                    const guid = uuid.v4();
                    onAction(urlList[0], guid);
                    bannerAnalyticsCallback('bannerCTAClicked',
                        {
                            analytics: { ...analytics, guid, url: urlList[0].url },
                            CTAText: textList[2],
                            ingestId: urlList[0].ingestId,
                            name,
                            ingestDescription,
                        },
                        appConfig,
                        appsSkeletonModel);
                }}
            />)
    );
};

const renderContent = ({
    textList, textColor, onAction, appData, urlList, imageList,
    name, ingestDescription, appConfig, appsSkeletonModel, analytics,
    allAppsModel, loadBackUpImages, backUpImageList, setBackUpImageList,
}) => {
    const style = { color: textColor };
    let iconUrl = appData.productIconUrl;
    if (loadBackUpImages && backUpImageList.length > 3) {
        [,,, iconUrl] = backUpImageList;
    } else if (imageList.length > 3) {
        [,,, iconUrl] = imageList;
    }

    return (
        <div className="content-wrapper">
            <img
                alt="Product Icon"
                src={iconUrl}
                onError={() => {
                    if (!loadBackUpImages) {
                        setBackUpImageList(true);
                    }
                }}
                className="product-icon" />
            <div className="title" style={style}>{textList[0]}</div>
            <div className="desc" style={style}>{textList[1]}</div>
            {(allAppsModel && shouldShowProdCTA(urlList[0], allAppsModel))
                ? (
                    <ProductCTA
                        allAppsModel={allAppsModel}
                        urlData={urlList[0]}
                        analytics={analytics}
                        name={name}
                        appsSkeletonModel={appsSkeletonModel}
                        appConfig={appConfig}
                        textColor={textColor} />
                )
                : renderDefaultCTA({
                    textList,
                    analytics,
                    urlList,
                    name,
                    ingestDescription,
                    appConfig,
                    onAction,
                    appsSkeletonModel,
                })}
        </div>
    );
};
const renderCards = (bannerItem) => {
    const {
        imageList, textList, loadBackUpImages, backUpImageList,
    } = bannerItem;
    const imagesToRender = [];
    const srcList = loadBackUpImages ? backUpImageList : imageList;
    srcList.forEach((imgSrc, index) => {
        if (textList.length > index + 3) {
            imagesToRender.push(
                <div className="card">
                    {/* Change alt tag to card-name if it is available in data */}
                    <img
                        alt={imgSrc}
                        className="image"
                        onError={() => {
                            if (!loadBackUpImages) {
                                bannerItem.setBackUpImageList(true);
                            }
                        }}
                        src={imgSrc} />
                    <div className="name">
                        {textList[index + 3]}
                    </div>
                </div>
            );
        }
    });

    return (
        <div className="cards-wrapper">
            {imagesToRender}
        </div>
    );
};

@observer
class ContextBannerItemLayout extends Component {
    renderGuid = uuid.v4();

    componentDidMount() {
        const {
            bannerItem: {
                analytics, textList, name, urlList, ingestDescription,
            },
            appConfig,
            appsSkeletonModel,
        } = this.props;

        analytics.bannerAnalyticsCallback('bannerRendered', {
            analytics,
            CTAText: textList[2],
            ingestId: urlList[0] && urlList[0].ingestId,
            name,
            ingestDescription,
            renderGuid: this.renderGuid,
        }, appConfig, appsSkeletonModel);
    }

    render() {
        const {
            bannerItem, appConfig, appsSkeletonModel, allAppsModel,
        } = this.props;
        const { backgroundColor } = bannerItem;

        return validateVitalData(bannerItem) && (
            <div
                className="context-banner-layout"
                render-guid={this.renderGuid}
                style={{ backgroundColor }}>
                <div className="spacer-div" />
                {renderContent({
                    ...bannerItem, appConfig, appsSkeletonModel, allAppsModel,
                })}
                {renderCards(bannerItem)}
                <div className="spacer-div" />
            </div>
        );
    }
}

export default ContextBannerItemLayout;
